import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { VendorInvoiceScanProposal } from '@services/vendor-invoice-scan.service';

export interface InvoiceScanDialogData {
  proposal?: VendorInvoiceScanProposal | null;
  vendorName?: string;
  objectUrl?: string;
  fileType?: string;
  loading?: boolean;
}

@Component({
  selector: 'app-invoice-scan-dialog',
  templateUrl: './invoice-scan-dialog.component.html',
  styleUrls: ['./invoice-scan-dialog.component.css'],
})
export class InvoiceScanDialogComponent implements OnInit {
  form: FormGroup | null = null;
  loading = true;
  previewReady = false;
  safeObjectUrl: SafeResourceUrl | null = null;
  safeDownloadUrl: SafeUrl | null = null;

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef,
    private dialogRef: MatDialogRef<InvoiceScanDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InvoiceScanDialogData
  ) {}

  ngOnInit(): void {
    this.loading = this.data.loading !== false && !this.data.proposal;
    if (this.data.objectUrl) {
      this.safeObjectUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        this.data.objectUrl
      );
      this.safeDownloadUrl = this.sanitizer.bypassSecurityTrustUrl(
        this.data.objectUrl
      );
    }
    if (this.data.proposal) {
      this.applyProposal(this.data.proposal);
    }
  }

  applyProposal(proposal: VendorInvoiceScanProposal): void {
    this.data.proposal = proposal;
    this.previewReady = !(proposal.previewPages || []).length;
    this.buildForm();
    this.loading = false;
    this.cdRef.detectChanges();
  }

  onPreviewLoad(): void {
    this.previewReady = true;
  }

  get showSpinner(): boolean {
    return this.loading || (!!this.previewPages.length && !this.previewReady);
  }

  private buildForm(): void {
    const header = this.data.proposal?.header || {};
    const matched = this.data.proposal?.match?.matchedLines || [];
    this.form = this.fb.group({
      invoiceNumber: [header.invoiceNumber || '', Validators.required],
      date: [header.invoiceDate || '', Validators.required],
      shipping: [
        header.shipping != null ? header.shipping : 0,
        Validators.required,
      ],
      tax: [header.tax != null ? header.tax : 0, Validators.required],
      total: [header.total != null ? header.total : ''],
      initials: ['', Validators.required],
      lines: this.fb.array(
        matched.map((row) =>
          this.fb.group({
            receive: [
              {
                value:
                  !!row.receive &&
                  !row.line?.isFreight &&
                  !row.line?.isAccessory,
                disabled: !!(
                  row.line?.isFreight ||
                  row.line?.isAccessory ||
                  !row.match
                ),
              },
            ],
            orderId: [row.match?.orderId || null],
            model: [row.line?.model || ''],
            color: [row.line?.color || row.line?.colorCode || ''],
            size: [row.line?.size || ''],
            quantity: [row.line?.quantity || 1],
            unitPrice: [row.line?.unitPrice],
            flags: [row.flags || []],
            confidence: [row.confidence || 'low'],
            isFreight: [!!row.line?.isFreight],
            isAccessory: [!!row.line?.isAccessory],
            description: [row.line?.description || ''],
          })
        )
      ),
    });
  }

  get lines(): FormArray {
    return this.form?.get('lines') as FormArray;
  }

  get exceptions() {
    return this.data.proposal?.match?.exceptions || [];
  }

  get unreadable() {
    return this.data.proposal?.match?.unreadableFields || [];
  }

  get previewPages(): string[] {
    return this.data.proposal?.previewPages || [];
  }

  get isImage(): boolean {
    return (this.data.fileType || '').startsWith('image/');
  }

  lowConfidence(control: FormGroup): boolean {
    return (
      control.value.confidence === 'low' ||
      (control.value.flags || []).includes('UNMATCHED_INVOICE_LINE')
    );
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    if (!this.form || this.form.invalid) {
      this.form?.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const selectedOrderIds = (raw.lines || [])
      .filter((line) => line.receive && line.orderId)
      .map((line) => String(line.orderId));

    this.dialogRef.close({
      confirmed: true,
      invoiceNumber: String(raw.invoiceNumber || '').trim(),
      date: raw.date,
      shipping: raw.shipping,
      tax: raw.tax,
      total: raw.total,
      initials: String(raw.initials || '').trim().toUpperCase(),
      selectedOrderIds,
      invoiceAttachment: this.data.proposal?.invoiceAttachment || null,
      proposal: this.data.proposal,
    });
  }
}
